<template>
    <div class="map" ref="mapElement"></div>
    <MapMarkerCategories class="map__categories" :categories="markerCategories" />
</template>
<script setup>
import { ref, reactive, onMounted, watch } from 'vue';
import { themeDir } from '@utils/theme.js';
import { apiUrl } from '@utils/api.js';
import axios from 'axios';
import MapMarkerCategories from './MapMarkerCategories.vue';

const props = defineProps({
    // The Mapbox access token
    token: {
        type: String,
        required: true,
    },
    // The latitude of the map centre
    lat: {
        type: Number,
        required: true,
    },
    // The longitude of the map centre
    lng: {
        type: Number,
        required: true,
    },
    // The initial zoom level of the map
    zoom: {
        type: Number,
        required: false,
        default: 14,
    },
    // The location ID to fetch POIs from
    locationId: {
        type: Number
    },
    // Whether to show a marker at the map centre
    showDefaultMarker: {
        type: Boolean,
        default: false,
    },
    // The title of the default marker
    defaultMarkerTitle: {
        type: String
    }
});

// The map element
const mapElement = ref(null);

// The map instance
let map = null;

// The displayed marker categories
const markerCategories = reactive({});

// The displayed markers
const markers = [];

// Watch for changes to the marker categories and update the markers accordingly
watch(markerCategories, (newVal, oldVal) => {
    markers.forEach(marker => {
        if (markerCategories[marker.almeroType].enabled) {
            marker.getElement().style.display = 'block';
        } else {
            marker.getElement().style.display = 'none';
        }
    });
}, { deep: true });


/**
 * Add a marker to the map
 * 
 * @param {*} options 
 */
const addMarker = (options) => {
    const defaultOptions = {
        title: null,
        icon: `${themeDir}/images/maps/icon-default.svg`,
        color: null,
        type: "Default"
    };

    const { map, lat, lng, title, color, icon, type } = { ...defaultOptions, ...options };

    if (!map || !lat || !lng) {
        return;
    }

    const marker = document.createElement('div');
    const markerImage = document.createElement('img');
    marker.className = 'marker';
    if (color) {
        marker.style.backgroundColor = color;
    }
    markerImage.className = 'marker__image';
    markerImage.src = icon;
    marker.appendChild(markerImage);
    const m = new mapboxgl.Marker(marker)
        .setLngLat([lng, lat]);

    if (title) {
        marker.classList.add('marker--has-popup');
        m.setPopup(new mapboxgl.Popup({
            offset: 17.5,
            closeButton: false,
            closeOnClick: false
        }).setHTML(title));

        marker.addEventListener('mouseenter', () => {
            m.getPopup().addTo(map);
        });

        marker.addEventListener('mouseleave', () => {
            m.getPopup().remove();
        });
    }

    m.almeroType = type;

    m.addTo(map);
    markers.push(m);

    return m;
};

onMounted(async () => {
    // Set the mapbox token
    mapboxgl.accessToken = props.token;

    // Create the map
    map = new mapboxgl.Map({
        container: mapElement.value,
        center: [props.lng, props.lat],
        zoom: props.zoom
    });

    // Add the navigation control
    map.addControl(new mapboxgl.NavigationControl({
        showCompass: false,
        showZoom: true
    }));

    // Disable scroll zoom so people can scroll the page without the map
    // capturing the scroll event
    map.scrollZoom.disable();

    // Resize the map when a DOM event is fired (e.g. when the media tab panel
    // is shown)
    window.addEventListener('map-resize', (e) => {
        setTimeout(() => {
            map.resize();
        }, 10);
    });

    // Keep a reference to the default marker
    let defaultMarker = null;

    // Add the default marker if required
    if (props.showDefaultMarker) {
        defaultMarker = addMarker({
            map,
            lat: props.lat,
            lng: props.lng,
            title: props.defaultMarkerTitle,
            color: 'var(--wp--preset--color--dark-blue)',
            icon: `${themeDir}/images/maps/icon-default.svg`
        });
        markerCategories["Default"] = {
            enabled: true,
            color: 'var(--wp--preset--color--dark-blue)',
            icon: `${themeDir}/images/maps/icon-default.svg`,
            name: "Misc"
        };
    }

    // Fetch the POIs for the location
    if (props.locationId > 0) {
        const response = await axios.get(`${apiUrl}/pois?post_id=${props.locationId}`);
        const pois = response.data;
        if (pois.length > 0) {
            pois.forEach(poi => {
                addMarker({
                    map,
                    lat: poi.lat,
                    lng: poi.lng,
                    title: poi.name,
                    color: poi.color,
                    icon: poi.icon,
                    type: poi.type
                });

                markerCategories[poi.type] = {
                    enabled: true,
                    color: poi.color,
                    icon: poi.icon,
                    name: poi.type
                };
            });
        }

        // Bring default marker to front
        if (defaultMarker) {
            defaultMarker.getElement().style.zIndex = 1000;
        }
    }
});

</script>